import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, brl, API } from "../lib/api";
import { AdminGate, AdminShell } from "./AdminGate";
import { toast } from "sonner";
import { Map, Save, AlertTriangle, ArrowLeft } from "lucide-react";

const formatAddr = (a) =>
  a ? [a.logradouro && a.numero ? `${a.logradouro}, ${a.numero}` : "", a.complemento, a.bairro, a.cidade && a.uf ? `${a.cidade} - ${a.uf}` : a.cidade, a.cep].filter(Boolean).join(", ") : "-";

function Section({ title, children, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid={testId}>
      <h3 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Row({ label, value, strong, testId }) {
  return (
    <div className="flex justify-between gap-3 py-1 text-sm" data-testid={testId}>
      <span className="text-slate-500">{label}</span>
      <span className={`text-right ${strong ? "font-heading font-bold text-navy" : "text-slate-700"}`}>{value}</span>
    </div>
  );
}

function Detail({ user, quoteId }) {
  const [q, setQ] = useState(null);
  const [settings, setSettings] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editReason, setEditReason] = useState("");
  const [distValor, setDistValor] = useState("");
  const [distCategorias, setDistCategorias] = useState(["pequeno", "medio", "grande"]);
  const [distModo, setDistModo] = useState("todos");
  const [distSel, setDistSel] = useState([]);
  const [distMotoristas, setDistMotoristas] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (distModo !== "selecionar" || !q) return;
    const data = q.agendamento?.data || "";
    const horario = q.agendamento?.horario || "";
    setDistMotoristas(null);
    api.get(`/admin/motoristas/disponiveis?data=${data}&horario=${horario}`)
      .then((r) => setDistMotoristas(r.data.motoristas))
      .catch(() => setDistMotoristas([]));
  }, [distModo, q?.quote_id]);

  const load = () => {
    api.get(`/admin/quotes/${quoteId}`).then((r) => {
      setQ(r.data);
      setEditValue(r.data.final_value);
    }).catch(() => toast.error("Orçamento não encontrado"));
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
  };

  useEffect(load, [quoteId]);

  if (!q) {
    return (
      <AdminShell user={user} title="Orçamento">
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-[#F5B301] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminShell>
    );
  }

  const p = q.pricing || {};
  const isManual = p.manual_evaluation || q.manual_evaluation_override;

  const navUrl = () => {
    if (settings?.navigation_app === "waze") {
      const coords = q.route?.coords || [];
      const last = coords[coords.length - 1];
      if (last) return `https://waze.com/ul?ll=${last.lat},${last.lon}&navigate=yes`;
      return `https://waze.com/ul?q=${encodeURIComponent(formatAddr(q.destination))}&navigate=yes`;
    }
    const origin = encodeURIComponent(formatAddr(q.origin));
    const dest = encodeURIComponent(formatAddr(q.destination));
    const wp = (q.stops || []).map((s) => encodeURIComponent(formatAddr(s))).join("|");
    return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}${wp ? `&waypoints=${wp}` : ""}`;
  };

  const saveEdit = async () => {
    const v = parseFloat(String(editValue).replace(",", "."));
    if (isNaN(v) || v < 0) return toast.error("Informe um valor válido");
    setSaving(true);
    try {
      const r = await api.patch(`/admin/quotes/${quoteId}`, { final_value: v, reason: editReason });
      setQ(r.data);
      toast.success("Orçamento atualizado!");
    } catch {
      toast.error("Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const toggleManual = async () => {
    try {
      const r = await api.patch(`/admin/quotes/${quoteId}`, { manual_evaluation: !isManual });
      setQ(r.data);
    } catch {
      toast.error("Falha ao atualizar");
    }
  };

  const setStatus = async (status) => {
    try {
      const r = await api.patch(`/admin/quotes/${quoteId}`, { status });
      setQ(r.data);
    } catch {
      toast.error("Falha ao atualizar status");
    }
  };

  return (
    <AdminShell user={user} title={`Orçamento ${q.quote_id}`}>
      <div className="px-4 mt-3 space-y-3">
        <Link to="/admin" data-testid="back-to-list" className="inline-flex items-center gap-1 text-xs text-slate-500">
          <ArrowLeft className="w-3.5 h-3.5" /> Voltar à lista
        </Link>

        {isManual && (
          <div className="flex gap-2.5 bg-amber-50 border border-amber-200 rounded-2xl p-3.5" data-testid="admin-manual-badge">
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
            <div>
              <p className="text-xs font-bold text-amber-700">AVALIAÇÃO MANUAL</p>
              {(p.manual_reasons || []).map((r, i) => (
                <p key={i} className="text-[11px] text-amber-600">• {r}</p>
              ))}
            </div>
          </div>
        )}

        <Section title="Cliente" testId="admin-section-cliente">
          <Row label="Nome" value={q.cliente?.nome} testId="admin-cliente-nome" />
          <Row label="Telefone" value={q.cliente?.telefone} testId="admin-cliente-telefone" />
          <Row label="E-mail" value={q.cliente?.email || "-"} />
          {q.indicacao && (
            <div className="mt-1.5" data-testid="admin-indicado-por">
              <span className="text-[11px] font-extrabold bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full">
                INDICADO POR: {q.indicacao.nome} — {q.indicacao.id_publico}
              </span>
            </div>
          )}
          <Row label="Corridas finalizadas" value={q.cliente_corridas ?? 0} testId="admin-cliente-corridas" />
          {(q.cliente_corridas ?? 0) > 2 && (
            <div className="mt-1.5" data-testid="cliente-recorrente-badge">
              <span className="text-[11px] font-extrabold bg-[#F5B301] text-navy px-3 py-1 rounded-full">⭐ CLIENTE RECORRENTE</span>
            </div>
          )}
          <Row label="Data desejada" value={q.agendamento?.data ? q.agendamento.data.split("-").reverse().join("/") : "-"} testId="admin-agendamento-data" />
          <Row label="Horário desejado" value={q.agendamento?.horario || "-"} testId="admin-agendamento-horario" />
          <Row label="Status" value={q.status || "novo"} />
          <div className="flex gap-2 mt-2">
            {["novo", "em_negociacao", "fechado", "cancelado"].map((s) => (
              <button
                key={s}
                data-testid={`status-${s}`}
                onClick={() => setStatus(s)}
                className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${
                  (q.status || "novo") === s ? "bg-navy text-[#F5B301]" : "bg-slate-100 text-slate-500"
                }`}
              >
                {s.replace("_", " ")}
              </button>
            ))}
          </div>
        </Section>

        {q.reservation && (
          <Section title="Reserva · Confirmação do Pix" testId="admin-section-reserva">
            <Row label="Nº reserva" value={q.reservation.numero_reserva} strong testId="admin-reserva-numero" />
            <Row label="Nº contrato" value={q.reservation.numero_contrato} testId="admin-reserva-contrato" />
            <Row label="Forma de pagamento" value={q.reservation.forma_texto} testId="admin-reserva-forma" />
            <Row label="Sinal (Pix)" value={brl(q.reservation.sinal)} />
            <Row
              label="Status do Pix"
              value={{
                confirmado: "🟢 PIX CONFIRMADO — RESERVA CONFIRMADA",
                aguardando_confirmacao: "🟡 PAGAMENTO INFORMADO — AGUARDANDO CONFIRMAÇÃO",
                recusado: "🔴 PAGAMENTO NÃO CONFIRMADO — AGENDAMENTO CANCELADO",
                expirado: "⚫ RESERVA EXPIRADA",
              }[q.reservation.pix_status] || "🟡 PAGAMENTO INFORMADO — AGUARDANDO CONFIRMAÇÃO"}
              strong
              testId="admin-pix-status"
            />
            {q.reservation.pix_informado_em && (
              <Row label="Cliente informou em" value={new Date(q.reservation.pix_informado_em).toLocaleString("pt-BR")} testId="admin-pix-informado" />
            )}
            {q.reservation.pix_confirmado_em && (
              <Row label="Empresa confirmou em" value={new Date(q.reservation.pix_confirmado_em).toLocaleString("pt-BR")} testId="admin-pix-confirmado" />
            )}
            <Row label="Aceite em" value={q.reservation.aceite_at ? new Date(q.reservation.aceite_at).toLocaleString("pt-BR") : "-"} />
            {(!q.reservation.pix_status || q.reservation.pix_status === "aguardando_confirmacao") && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  data-testid="confirmar-pix-btn"
                  onClick={async () => {
                    try {
                      await api.post(`/admin/reservations/${q.reservation.reservation_id}/confirmar-pix`);
                      toast.success("Pix confirmado — data oficialmente reservada!");
                      load();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Falha ao confirmar o Pix");
                    }
                  }}
                  className="py-3 rounded-xl bg-emerald-600 text-white font-heading font-bold text-xs active:scale-95 transition-transform"
                >
                  CONFIRMAR RECEBIMENTO DO PIX
                </button>
                <button
                  data-testid="recusar-pix-btn"
                  onClick={async () => {
                    const motivo = window.prompt("Motivo da recusa (opcional):") || "";
                    if (!window.confirm("Confirmar a recusa? A data será liberada.")) return;
                    try {
                      await api.post(`/admin/reservations/${q.reservation.reservation_id}/recusar`, { motivo });
                      toast.success("Pagamento não confirmado — data liberada.");
                      load();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Falha ao recusar");
                    }
                  }}
                  className="py-3 rounded-xl bg-red-500 text-white font-heading font-bold text-xs active:scale-95 transition-transform"
                >
                  NÃO CONFIRMAR / RECUSAR
                </button>
              </div>
            )}
            <a
              data-testid="admin-reserva-pdf-btn"
              href={`${API}/reservations/${q.reservation.reservation_id}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 flex items-center justify-center gap-2 w-full py-3 rounded-xl border-2 border-navy text-navy font-heading font-bold text-sm active:scale-95 transition-transform"
            >
              VISUALIZAR CONTRATO (PDF)
            </a>
          </Section>
        )}

        <Section title="Endereços" testId="admin-section-enderecos">
          <Row label="Embarque" value={formatAddr(q.origin)} testId="admin-addr-origin" />
          {(q.stops || []).map((s, i) => (
            <Row key={i} label={`Parada ${i + 1}`} value={`${formatAddr(s)}${s.observacao ? ` (${s.observacao})` : ""}`} testId={`admin-addr-stop-${i}`} />
          ))}
          <Row label="Destino" value={formatAddr(q.destination)} testId="admin-addr-destination" />
        </Section>

        <Section title="Rota" testId="admin-section-rota">
          <Row label="Quilometragem" value={`${q.route?.distance_km} km`} strong testId="admin-route-km" />
          <Row label="Tempo estimado" value={`~${q.route?.duration_min} min`} />
          <Row label="Cálculo via" value={q.route?.provider === "google" ? "Google Maps" : "Estimativa (OSRM)"} />
          <Row label="Navegação" value={settings?.navigation_app === "waze" ? "Waze" : "Google Maps"} />
          <a
            data-testid="abrir-rota-btn"
            href={navUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-navy text-[#F5B301] font-heading font-bold text-sm active:scale-95 transition-transform"
          >
            <Map className="w-4 h-4" /> ABRIR ROTA
          </a>
        </Section>

        <Section title="Itens" testId="admin-section-itens">
          {Object.entries(q.items || {}).map(([name, qty]) => (
            <Row key={name} label={name} value={`x${qty}`} />
          ))}
          {q.outros_itens && <Row label="Outros" value={q.outros_itens} />}
          {(q.fotos || []).length > 0 && (
            <div className="grid grid-cols-3 gap-2 mt-3" data-testid="admin-photos-grid">
              {q.fotos.map((path, i) => (
                <a key={i} href={`${API}/files/${path}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-photo-${i}`}>
                  <img src={`${API}/files/${path}`} alt="" className="w-full h-20 object-cover rounded-lg border border-slate-200" />
                </a>
              ))}
            </div>
          )}
        </Section>

        <Section title="Acesso" testId="admin-section-acesso">
          <Row label="Acesso" value={q.acesso?.nivel} />
          <Row label="Caminhão para próximo" value={q.acesso?.caminhaoPara} />
          {q.acesso?.distancia && <Row label="Distância caminhão" value={q.acesso.distancia} />}
          <Row label="Escada" value={q.escada?.existe ? `Sim (${q.escada.lances >= 4 ? "4 ou mais" : q.escada.lances} lance(s))` : "Não"} />
          <Row label="Elevador" value={q.elevador?.existe ? `Sim (comporta: ${q.elevador.comporta})` : "Não"} />
          <Row label="Dificuldade (interno)" value={`${p.difficulty_grade} · ${Math.round((p.difficulty_pct || 0) * 100)}%`} strong testId="admin-difficulty" />
        </Section>

        <Section title="Ajudantes (interno)" testId="admin-section-ajudantes">
          <Row label="Quantidade" value={p.helpers_qty > 4 ? "mais de 4" : p.helpers_qty || 0} />
          <Row label="Valor por ajudante" value={brl(p.helper_unit)} testId="admin-helper-unit" />
          <Row label="Total ajudantes" value={brl(p.helpers_total)} strong testId="admin-helper-total" />
        </Section>

        <Section title="Carona (interno)" testId="admin-section-carona">
          <Row label="Carona" value={p.carona ? "Sim" : "Não"} />
          {p.carona && <Row label="Quilometragem" value={`${p.distance_km} km`} />}
          {p.carona && <Row label="Adicional interno" value={brl(p.carona_cost)} strong testId="admin-carona-cost" />}
        </Section>

        <Section title="Serviços" testId="admin-section-servicos">
          <Row label="Embalagem" value={q.servicos?.embalagem ? "Sim — sob consulta" : "Não"} />
          <Row label="Desmontagem" value={q.servicos?.desmontagem ? "Sim — sob consulta" : "Não"} />
          <Row label="Montagem" value={q.servicos?.montagem ? "Sim — sob consulta" : "Não"} />
        </Section>

        {(q.pricing?.servicos_adicionais || []).length > 0 && (
          <Section title="Serviços adicionais (com preço)" testId="admin-section-servicos-adicionais">
            {q.pricing.servicos_adicionais.map((s, i) => (
              <Row key={i} label={s.tipo === "pacote" ? `📦 ${s.nome}` : `${s.nome} (${s.tipo})`}
                value={`x${s.qtd} · ${brl(s.valor)}`} testId={`admin-serv-add-${i}`} />
            ))}
            <Row label="Total serviços adicionais" value={brl(q.pricing.servicos_total || 0)} strong testId="admin-serv-add-total" />
            {(q.servicos_fotos || []).length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-3" data-testid="admin-servicos-fotos-grid">
                {q.servicos_fotos.map((path, i) => (
                  <a key={i} href={`${API}/files/${path}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-servicos-foto-${i}`}>
                    <img src={`${API}/files/${path}`} alt="" className="w-full h-20 object-cover rounded-lg border border-slate-200" />
                  </a>
                ))}
              </div>
            )}
          </Section>
        )}

        <Section title="Distribuir para motoristas" testId="admin-section-distribuir">
          {q.job_id ? (
            <div className="flex items-center justify-between">
              <p className="text-xs text-emerald-600 font-bold">✅ Corrida disponibilizada ({q.job_id})</p>
              <Link to="/admin/corridas" data-testid="ver-corridas-link" className="text-[11px] font-bold text-navy underline">Ver corridas</Link>
            </div>
          ) : (
            <div className="space-y-2.5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Enviar para</p>
              <div className="grid grid-cols-2 gap-2" data-testid="distribuir-modo">
                {[["todos", "TODOS OS DISPONÍVEIS"], ["selecionar", "SELECIONAR MOTORISTAS"]].map(([v, l]) => (
                  <button key={v} data-testid={`distribuir-modo-${v}`} onClick={() => setDistModo(v)}
                    className={`py-2.5 rounded-xl border-2 text-[10px] font-bold active:scale-95 transition-all ${
                      distModo === v ? "border-[#F5B301] bg-gold-light text-navy" : "border-slate-200 bg-white text-slate-400"}`}>
                    {l}
                  </button>
                ))}
              </div>

              {distModo === "selecionar" && (
                <div className="space-y-1.5" data-testid="distribuir-motoristas">
                  {distMotoristas === null && <p className="text-xs text-slate-400 py-2">Carregando motoristas...</p>}
                  {distMotoristas !== null && distMotoristas.length === 0 && (
                    <p className="text-xs text-red-500 py-2" data-testid="distribuir-nenhum">Nenhum motorista ativo cadastrado.</p>
                  )}
                  {(distMotoristas || []).map((m) => (
                    <button key={m.driver_id} data-testid={`dist-motorista-${m.driver_id}`} disabled={!m.elegivel}
                      onClick={() => setDistSel((cur) => cur.includes(m.driver_id) ? cur.filter((x) => x !== m.driver_id) : [...cur, m.driver_id])}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border-2 text-left text-xs font-bold transition-all ${
                        !m.elegivel ? "border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed"
                        : distSel.includes(m.driver_id) ? "border-[#F5B301] bg-gold-light text-navy" : "border-slate-200 bg-white text-slate-600"}`}>
                      <span>{m.nome} <span className="font-normal text-[10px]">({(m.portes || []).join("/") || "sem porte"})</span></span>
                      <span className="text-[9px] font-bold">
                        {m.bloqueado ? "BLOQUEADO" : m.conflito ? "OCUPADO NO HORÁRIO" : "DISPONÍVEL"}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Portes da corrida</p>
              <div className="grid grid-cols-3 gap-2" data-testid="distribuir-categorias">
                {[["pequeno", "Pequeno"], ["medio", "Médio"], ["grande", "Grande"]].map(([v, l]) => (
                  <button
                    key={v}
                    data-testid={`categoria-${v}`}
                    onClick={() => setDistCategorias((cur) => cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v])}
                    className={`py-2.5 rounded-xl border-2 text-[11px] font-bold active:scale-95 transition-all ${
                      distCategorias.includes(v) ? "border-[#F5B301] bg-gold-light text-navy" : "border-slate-200 bg-white text-slate-400"}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  data-testid="distribuir-valor-input"
                  inputMode="decimal"
                  className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#F5B301]"
                  placeholder="Valor a pagar ao motorista (R$)"
                  value={distValor}
                  onChange={(e) => setDistValor(e.target.value)}
                />
                <button
                  data-testid="distribuir-btn"
                  onClick={async () => {
                    const v = parseFloat(String(distValor).replace(",", "."));
                    if (isNaN(v) || v <= 0) return toast.error("Informe o valor a pagar ao motorista");
                    if (distCategorias.length === 0) return toast.error("Selecione pelo menos um porte para o disparo");
                    if (distModo === "selecionar" && distSel.length === 0) return toast.error("Selecione pelo menos um motorista");
                    try {
                      const r = await api.post("/admin/jobs", {
                        quote_id: q.quote_id, valor_motorista: v,
                        categorias_alvo: distCategorias,
                        motoristas_ids: distModo === "selecionar" ? distSel : [],
                      });
                      toast.success(`Corrida enviada para ${r.data.enviada_para?.length || 0} motorista(s)!`);
                      setDistSel([]);
                      load();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Falha ao disponibilizar");
                    }
                  }}
                  className="px-4 py-3 rounded-xl bg-navy text-[#F5B301] font-heading font-bold text-xs active:scale-95 transition-transform"
                >
                  DISPONIBILIZAR
                </button>
              </div>
            </div>
          )}
          <p className="text-[11px] text-slate-400 mt-2">O motorista verá somente as informações operacionais e o valor oferecido — nunca os valores do cliente.</p>
        </Section>

        <Section title="Precificação (interno)" testId="admin-section-pricing">
          <Row label="Valor base" value={brl(p.base)} testId="admin-price-base" />
          {p.piano_cofre_extra > 0 && <Row label={`Itens pesados (piano×${p.piano_qty || 0} / cofre×${p.cofre_qty || 0})`} value={brl(p.piano_cofre_extra)} testId="admin-piano-cofre" />}
          {p.escada_degraus > 0 && <Row label={`Escada (${p.escada_degraus} degraus)`} value={p.escada_tier === "maximo" ? "Grau máximo de dificuldade" : p.escada_tier === "elevado" ? "Faixa elevada" : "Faixa normal"} testId="admin-escada-tier" />}
          <Row label="Quilometragem" value={`${p.distance_km} km`} />
          <Row label="R$/km" value={brl(p.km_rate)} testId="admin-price-kmrate" />
          <Row label="Custo km" value={brl(p.km_cost)} testId="admin-price-kmcost" />
          <Row label="Dificuldade" value={`${p.difficulty_grade} (${Math.round((p.difficulty_pct || 0) * 100)}%)`} />
          <Row label="Adicional dificuldade" value={brl(p.difficulty_amount)} testId="admin-price-difficulty" />
          <Row label="Ajudantes" value={brl(p.helpers_total)} />
          <Row label="Pedágio (base)" value={brl(p.toll_raw ?? 0)} />
          <Row label="Pedágio cobrado" value={brl(p.toll)} testId="admin-price-toll" />
          <Row label="Subtotal" value={brl(p.subtotal ?? p.total_calculated)} />
          {(p.distance_surcharge_amount ?? 0) > 0 && (
            <Row label="Adicional distância >75km (15%)" value={brl(p.distance_surcharge_amount)} testId="admin-price-distance-surcharge" />
          )}
          <Row label="Carona" value={brl(p.carona_cost)} />
          <div className="border-t border-slate-100 mt-2 pt-2">
            <Row label="Valor calculado" value={brl(p.total_calculated)} strong testId="admin-price-calculated" />
            <Row label="Valor final" value={brl(q.final_value)} strong testId="admin-price-final" />
          </div>
          {q.value_override && (
            <p className="text-[11px] text-amber-600 mt-1" data-testid="admin-override-info">
              Alterado de {brl(q.value_override.calculated)} para {brl(q.value_override.final)} por {q.value_override.admin}
              {q.value_override.reason ? ` — Motivo: ${q.value_override.reason}` : ""}
            </p>
          )}
        </Section>

        <Section title="Editar orçamento" testId="admin-section-edit">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Novo valor final (R$)</label>
          <input
            data-testid="edit-value-input"
            inputMode="decimal"
            className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#F5B301]"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
          />
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mt-3 block">Motivo da alteração</label>
          <input
            data-testid="edit-reason-input"
            className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#F5B301]"
            placeholder="Ex.: parada negociada, pedágio..."
            value={editReason}
            onChange={(e) => setEditReason(e.target.value)}
          />
          <button
            data-testid="save-edit-btn"
            onClick={saveEdit}
            disabled={saving}
            className="mt-3 w-full py-3 rounded-xl bg-navy text-[#F5B301] font-heading font-bold text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
          >
            <Save className="w-4 h-4" /> SALVAR VALOR
          </button>
          <button
            data-testid="toggle-manual-btn"
            onClick={toggleManual}
            className={`mt-2 w-full py-3 rounded-xl font-semibold text-xs border-2 active:scale-95 transition-transform ${
              isManual ? "border-amber-300 bg-amber-50 text-amber-700" : "border-slate-200 text-slate-500"
            }`}
          >
            {isManual ? "REMOVER AVALIAÇÃO MANUAL" : "MARCAR AVALIAÇÃO MANUAL"}
          </button>
        </Section>

        {(q.created_at || (q.edits || []).length > 0) && (
          <Section title="Histórico de alterações" testId="admin-section-history">
            <p className="text-[11px] text-slate-500 pb-1.5 border-b border-slate-50" data-testid="history-criado-em">
              📄 Criado em {q.created_at ? new Date(q.created_at).toLocaleString("pt-BR") : "—"}
              {q.editado_em ? ` · última alteração ${new Date(q.editado_em).toLocaleString("pt-BR")}` : ""}
            </p>
            {(q.edits || []).map((e, i) => (
              <div key={i} className="text-[11px] text-slate-500 py-1.5 border-b border-slate-50 last:border-0" data-testid={`history-entry-${i}`}>
                {e.acao === "edicao_cliente" ? (
                  <>
                    <p className="font-bold text-navy">✏️ Alterado pelo cliente — {new Date(e.em).toLocaleString("pt-BR")}</p>
                    {(e.campos || []).map((c, j) => (
                      <p key={j} className="mt-0.5">• {c.campo}: {c.de || "—"} → {c.para || "—"}</p>
                    ))}
                    {e.total_anterior !== undefined && e.total_anterior !== e.novo_total && (
                      <p className="mt-0.5 font-semibold text-amber-600">valor: {brl(e.total_anterior)} → {brl(e.novo_total)}</p>
                    )}
                  </>
                ) : (
                  <>
                    {e.new_value !== undefined && `${brl(e.old_value)} → ${brl(e.new_value)} · `}
                    {e.status && `status: ${e.status} · `}
                    {e.reason && `${e.reason} · `}
                    {e.admin} · {e.date ? new Date(e.date).toLocaleString("pt-BR") : ""}
                  </>
                )}
              </div>
            ))}
          </Section>
        )}
      </div>
    </AdminShell>
  );
}

export default function AdminQuoteDetail() {
  const { quoteId } = useParams();
  return <AdminGate>{(user) => <Detail user={user} quoteId={quoteId} />}</AdminGate>;
}
